import {mapRunningTimes} from '@domain/shared'
import {RcfaLinks} from '@hconnect/common/components/runningTimes'
import {RunningTime, RunningTimeDto} from '@hconnect/common/types'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloseIcon from '@mui/icons-material/Close'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import {
  Button,
  DialogActions,
  DialogContent,
  Typography,
  Stack,
  useMediaQuery,
  Theme
} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import {StoppageSplitDateTimeEditor} from '@widgets/common-running-times'
import {
  type OnDragEvent,
  type RunningTimesStoppageCodesByType,
  StackedBarChart
} from '@widgets/running-times'
import moment, {Moment} from 'moment-timezone'
import {useCallback, useState, FC, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {splitIntoThreeSegments, updateRunningTimesWithNewDate} from '../utils'

import {RuntimeSplitDurationDetails} from './RuntimeSplitDurationDetails'

type SplitRuntimeDialogProps = {
  plantId: string
  timezone: string
  runningTime: RunningTimeDto
  onClose: () => void
  stoppageCodesByType: RunningTimesStoppageCodesByType
  onSubmit: (runningTimes: [RunningTime, RunningTime, RunningTime]) => void
}

export const SplitRuntimeDialog: FC<SplitRuntimeDialogProps> = ({
  plantId,
  timezone,
  runningTime,
  onClose,
  stoppageCodesByType,
  onSubmit
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const [runningTimesWithGaps, setRunningTimesWithGaps] = useState<RunningTime[]>(
    mapRunningTimes(splitIntoThreeSegments(runningTime), timezone)
  )
  const dialogRef = useRef<HTMLDivElement>(null)

  const setSplitDate = useCallback((newDate: Moment, id?: number) => {
    setRunningTimesWithGaps((runTimes) => {
      const index = runTimes.findIndex((rt) => rt.id === id)
      const nextRunningTimes = updateRunningTimesWithNewDate(index, runTimes, newDate, true)
      return nextRunningTimes || runTimes
    })
  }, [])

  const handleResize: OnDragEvent = useCallback(
    ({percentEnd, totalRunningTimeMs, index}) => {
      if (index === undefined) return
      const offsetMs = percentEnd * totalRunningTimeMs
      const newDate = runningTimesWithGaps[0].beginTechnical?.clone().add(offsetMs, 'milliseconds')
      setRunningTimesWithGaps((rt) => {
        const nextRunningTimes = updateRunningTimesWithNewDate(index, rt, newDate, true)
        return nextRunningTimes ? nextRunningTimes : rt
      })
    },
    [runningTimesWithGaps]
  )

  const timeRange = {
    startDate: moment.utc(runningTimesWithGaps[0].beginTechnical).tz(timezone),
    endDate: moment.utc(runningTimesWithGaps[2].endTechnical).tz(timezone)
  }

  return (
    <Dialog open={true} onClose={onClose} PaperProps={{sx: {width: '514px'}}}>
      <DialogTitle>
        <Typography
          onClick={onClose}
          sx={{cursor: 'pointer'}}
          variant="subtitle2"
          color="primary"
          display="flex"
          alignItems="center"
          component="span"
          width="fit-content"
        >
          <ChevronLeftIcon />
          {t('splitRuntime.action.backToStoppageAnalysis')}
        </Typography>
        <Typography variant="h3" component="span">
          {t('splitRuntime.title')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{mt: 26}} data-test-id="split-runtime-dialog">
        {runningTime.beginTechnical && (
          <Stack spacing={4} ref={dialogRef}>
            <StackedBarChart
              useTechnicalDates
              sx={{height: 48, borderRadius: 0.75}}
              timeRange={timeRange}
              timezone={timezone}
              getResizeHandleTooltip={(rt, nextRt) =>
                rt.beginTechnical &&
                rt.endTechnical && (
                  <StoppageSplitDateTimeEditor
                    stoppageTimeRange={[rt.beginTechnical, nextRt?.endTechnical]}
                    timezone={timezone}
                    splitDate={rt.endTechnical}
                    setSplitDate={(newDate) => setSplitDate(newDate, rt.id)}
                    tooltipHeader={t('label.adjustAt')}
                  />
                )
              }
              renderTooltipFooter={({rootCauseFailureAnalyses}) =>
                rootCauseFailureAnalyses && (
                  <RcfaLinks
                    plantId={plantId}
                    rootCauseFailureAnalyses={rootCauseFailureAnalyses}
                  />
                )
              }
              isEditMode={true}
              showDraggableArea={false}
              onMove={!isMobile ? handleResize : undefined}
              data={runningTimesWithGaps}
              stoppageCodesByType={stoppageCodesByType}
              resizeHandlePlacement="top"
              resizeHandleContainerRef={dialogRef}
              resizeHandleTooltipDefaultIndex={0}
            />
            <RuntimeSplitDurationDetails runningTimes={runningTimesWithGaps} timezone={timezone} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          startIcon={<CloseIcon />}
          data-test-id="split-runtime-cancel-button"
        >
          {t('splitRuntime.action.cancel')}
        </Button>
        <Button
          onClick={() => {
            const runningTimes: [RunningTime, RunningTime, RunningTime] = [
              runningTimesWithGaps[0],
              runningTimesWithGaps[1],
              runningTimesWithGaps[2]
            ]
            onSubmit(runningTimes)
            onClose()
          }}
          color="primary"
          variant="contained"
          startIcon={<ContentCutIcon />}
          data-test-id="split-runtime-button"
        >
          {t('splitRuntime.action.split')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
